import { useCapabilitiesOptional } from "@/providers/CapabilitiesProvider";

/**
 * The SMS channel's two switches as the cockpit reads them (SMS channel wave).
 *
 * Fail closed and defensive on purpose: the flags ride `EventRoleDTO`
 * (`smsChannelEnabled`, `smsFixedModeEnabled`) through the CapabilitiesProvider,
 * a page outside the event shell gets the provider's degraded fallback, and an
 * older backend sends neither key. Only an explicit `true` turns anything on —
 * `undefined`, `null`, `"true"` and a missing provider all read "off", so no
 * SMS surface (field, column, consent row, tab) appears while the backend
 * switch is off.
 *
 * `CapabilitiesContextType` carries both as required booleans (the provider
 * coerces the DTO with `=== true`; its fallback says `false`). Every SMS
 * surface still reads them through these helpers — one reader style, and it
 * also holds for a partial context (test doubles, the degraded fallback).
 */
export function smsChannelEnabledOf(caps: unknown): boolean {
  return (
    !!caps &&
    typeof caps === "object" &&
    (caps as { smsChannelEnabled?: unknown }).smsChannelEnabled === true
  );
}

/** Fixed-recipient mode: needs the channel AND its own switch. */
export function smsFixedModeEnabledOf(caps: unknown): boolean {
  return (
    smsChannelEnabledOf(caps) &&
    (caps as { smsFixedModeEnabled?: unknown }).smsFixedModeEnabled === true
  );
}

/** `smsChannelEnabledOf` over the nearest CapabilitiesProvider (or its fallback). */
export function useSmsChannelEnabled(): boolean {
  return smsChannelEnabledOf(useCapabilitiesOptional());
}

/**
 * The built-in guest fields that exist only while the SMS channel is on. A
 * custom additional field of the same name is NOT one of them (it carries
 * `additionalField: true`) and is never touched.
 */
export const SMS_GUEST_FIELD_NAMES: ReadonlySet<string> = new Set([
  "smsOptIn",
  "smsOptInAt",
  "smsOptInSource",
]);

/** Drop the SMS built-ins from a field list unless the channel is on. */
export function withoutSmsFieldsUnless<T extends { name: string; additionalField?: boolean }>(
  fields: T[],
  enabled: boolean,
): T[] {
  if (enabled) return fields;
  if (!fields.some((f) => !f.additionalField && SMS_GUEST_FIELD_NAMES.has(f.name))) return fields;
  return fields.filter((f) => f.additionalField || !SMS_GUEST_FIELD_NAMES.has(f.name));
}

/**
 * A picker's list without the SMS built-ins unless they are OFFERED (SMS
 * plugin wave, F-6 / Q3: flag AND the event's SMS plugin, see
 * `useSmsFieldsOffered`). `keep` names fields that stay anyway (what a view,
 * a filter row or a mapping already holds, so it can still be seen and
 * removed). Custom additional fields of the same name are never touched.
 * Returns the same array when nothing would change.
 */
export function withoutUnofferedSmsFields<T extends { name: string; additionalField?: boolean }>(
  fields: T[],
  offered: boolean,
  keep: Iterable<string | null | undefined> = [],
): T[] {
  if (offered) return fields;
  const kept = new Set<string>();
  for (const k of keep) if (typeof k === "string") kept.add(k);
  const drops = (f: T) => !f.additionalField && SMS_GUEST_FIELD_NAMES.has(f.name) && !kept.has(f.name);
  return fields.some(drops) ? fields.filter((f) => !drops(f)) : fields;
}
